<template>
  <div>
    <v-hover v-slot="{ isHovering, props }">
      <v-card
        elevation="0"
        :loading="loading"
        tile
        v-bind="props"
        :disabled="loading"
      >
        <!-- Custom loader with color -->
        <template v-slot:loader="{ isActive }">
          <v-progress-linear
            :active="isActive"
            color="primary"
            height="3"
            indeterminate
          ></v-progress-linear>
        </template>

        <!-- Error Alert -->
        <v-alert
          v-if="error"
          type="error"
          class="mb-2"
          density="compact"
          variant="tonal"
        >
          {{ error }}
        </v-alert>

        <!-- Main Content -->
        <div v-else-if="hasData">
          <h2 class="text-h6 text-primary">
            <v-icon left color="secondary">mdi-windsock</v-icon>
            Wind
          </h2>
          <compass-direction :direction="latestDataPoint.direction" />
          Direction:
          <b>{{ latestDataPoint.direction.toFixed(0) }}°</b> ({{
            direction2txt(latestDataPoint.direction)
          }})
          <br />
          <b>{{ latestDataPoint.speed.toFixed(0) }} knots</b> ({{
            windspeed2txt(latestDataPoint.speed)
          }})
          <br />
          Gust: <b>{{ latestDataPoint.gust.toFixed(0) }} knots</b>
        </div>

        <!-- No Data Message -->
        <div v-else-if="!loading" class="text-center pa-4">
          <v-icon color="grey" size="large">mdi-windsock</v-icon>
          <p class="text-grey mt-2">No wind data available</p>
        </div>

        <!-- Hover Overlay -->
        <v-overlay
          :model-value="isHovering && !error && hasData"
          class="align-center justify-center"
          scrim="rgba(138, 177, 211, 0.4)"
          contained
          @click="show = true"
          style="cursor: pointer"
        >
          <v-btn variant="flat" color="secondary">See more info</v-btn>
        </v-overlay>
      </v-card>
    </v-hover>

    <!-- Overlay with detailed information -->
    <v-overlay
      v-model="show"
      :opacity="0.6"
      color="rgba(138, 177, 211)"
      z-index="7"
      class="d-flex align-center justify-center overlay-primary"
    >
      <v-card light class="chart-container" :max-width="cardMaxWidth">
        <v-container
          fluid
          class="pa-1 pt-0 d-flex flex-column justify-space-between"
        >
          <!-- First Row: Help Button, Timestamp, Close Button -->
          <v-row justify="space-between" align="center" class="top-row">
            <v-col cols="auto">
              <v-btn
                color="primary lighten-1"
                @click="help = !help"
                dark
                fab
                elevation="0"
                small
                class="mt-1"
              >
                <v-icon>mdi-help</v-icon>
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <v-btn
                color="primary"
                @click="show = false"
                dark
                fab
                elevation="0"
                small
                class="mt-1"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-row justify="center" align="center" class="time">
            <v-col cols="auto" class="text-center pa-0">
              <timestamp-status
                :latestTimestamp="latestTimestamp"
              ></timestamp-status>
            </v-col>
          </v-row>

          <!-- Error Alert in Overlay -->
          <v-alert v-if="error" type="error" class="ma-4" variant="tonal">
            {{ error }}
          </v-alert>

          <!-- Second Row: Rose Chart and Wind Chart -->
          <v-row
            v-else
            :class="
              display.smAndDown
                ? 'd-flex flex-row'
                : 'd-flex flex-row justify-space-between'
            "
            class="charts-row"
            align="stretch"
          >
            <!-- Rose Chart -->
            <v-col :cols="12" :md="12" :lg="6" :xl="5" class="chart-container">
              <PlotCustom
                v-if="hasData && roseData.length"
                class="plotly-chart"
                :data="roseData"
                :layout="roseLayout"
                :config="{ responsive: true, displayModeBar: false }"
              ></PlotCustom>
              <div v-else class="text-center">No rose data available.</div>
            </v-col>

            <!-- Wind Chart -->
            <v-col :cols="12" :md="12" :lg="6" :xl="5" class="chart-container">
              <PlotCustom
                v-if="hasData && data.length"
                class="plotly-chart"
                :data="data"
                :layout="layout"
                :config="{ responsive: true, displayModeBar: false }"
              ></PlotCustom>
              <div v-else class="text-center">No wind data available.</div>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-overlay>

    <!-- Help Overlay with Beaufort Scale -->
    <v-overlay
      v-model="help"
      :opacity="0.4"
      color="primary"
      z-index="7"
      class="d-flex align-center justify-center"
    >
      <v-container fluid class="pa-0 ma-0 pt-4">
        <v-row no-gutters>
          <v-col cols="12" md="10" lg="8" class="mx-auto pb-0">
            <v-alert color="secondary" class="pa-4 help-alert">
              <div class="help-header">
                <h3 class="text-h6">Beaufort Wind Scale</h3>
                <v-btn color="white" @click="help = !help" icon="mdi-close" size="small">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
              <p class="text-body-1 mb-4">
                The Beaufort scale classifies wind conditions from 0 (calm) to 12 (hurricane). It helps sailors 
                and meteorologists describe wind conditions based on observable sea conditions.
              </p>
              
              <div class="beaufort-grid">
                <v-card
  v-for="item in beaufortScale"
  :key="item.force"
  class="beaufort-card d-flex"
  elevation="2"
>
  <div 
    class="color-strip"
    :style="{ backgroundColor: getBeaufortColor(item.force) }"
  ></div>
  <div class="card-content flex-grow-1">
    <v-card-title class="text-h6 d-flex justify-space-between align-center">
      <span class="force-number">{{ item.force }}</span>
      <span class="force-name">{{ item.name }}</span>
    </v-card-title>
    <v-card-text>
      <div class="d-flex flex-column">
        <div class="wind-speed">
          <v-icon icon="mdi-weather-windy" class="mr-1"></v-icon>
          {{ item.knots }} knots
        </div>
        <div class="sea-conditions">
          <v-icon icon="mdi-waves" class="mr-1"></v-icon>
          {{ item.seaConditions }}
        </div>
      </div>
    </v-card-text>
  </div>
</v-card>
              </div>
            </v-alert>
          </v-col>
        </v-row>
      </v-container>
    </v-overlay>
  </div>
</template>

<script>
import PlotCustom from "@/components/PlotCustom.vue";
import { generateRose,  windPlotColours } from "@/plugins/helper.js";
import generateShapes from "@/plugins/verticalIntervals";
import windspeed2txt from "@/plugins/windSpeedClassification";
import direction2txt from "@/plugins/directionClassification";
import CompassDirection from "@/components/CompassDirection.vue";
import timestampStatus from "@/components/TimestampStatus.vue";
import { useDisplay } from "vuetify";
import { fetchWindData } from "@/Service/windService";

export default {
  name: "WindOverlay",
  components: {
    PlotCustom,
    timestampStatus,
    CompassDirection,
  },
  props: {
    site: {
      type: String,
      default: "Tasman_Bay 119",
      required: true,
    },
  },
  emits: ["noData", "latestDate"],

  data() {
    return {
      show: false,
      help: false,
      loading: true,
      hasData: false,
      error: null,
      latestDataPoint: {
        speed: 0,
        gust: 0,
        direction: 0,
      },
      data: [],
      roseData: [],
      refresher: null,
      interval: 300000,
      dtick: 4,
      latestTimestamp: null,
      highContrast: true,
      beaufortScale: [
        { force: 0, name: 'Calm', knots: '0-1', seaConditions: 'Sea surface smooth and mirror-like' },
        { force: 1, name: 'Light Air', knots: '1-3', seaConditions: 'Scaly ripples, no foam crests' },
        { force: 2, name: 'Light Breeze', knots: '4-6', seaConditions: 'Small wavelets, crests glassy' },
        { force: 3, name: 'Gentle Breeze', knots: '7-10', seaConditions: 'Large wavelets, crests begin to break' },
        { force: 4, name: 'Moderate Breeze', knots: '11-16', seaConditions: 'Small waves with breaking crests' },
        { force: 5, name: 'Fresh Breeze', knots: '17-21', seaConditions: 'Moderate waves, many whitecaps' },
        { force: 6, name: 'Strong Breeze', knots: '22-27', seaConditions: 'Large waves, white foam crests' },
        { force: 7, name: 'Near Gale', knots: '28-33', seaConditions: 'Sea heaps up, foam begins to streak' },
        { force: 8, name: 'Gale', knots: '34-40', seaConditions: 'Moderately high waves, breaking crests' },
        { force: 9, name: 'Strong Gale', knots: '41-47', seaConditions: 'High waves, dense foam' },
        { force: 10, name: 'Storm', knots: '48-55', seaConditions: 'Very high waves with overhanging crests' },
        { force: 11, name: 'Violent Storm', knots: '56-63', seaConditions: 'Exceptionally high waves' },
        { force: 12, name: 'Hurricane', knots: '64+', seaConditions: 'Air filled with foam and spray' }
      ]
    };
  },

  computed: {
    display() {
      return useDisplay();
    },
    cardMaxWidth() {
      const screenWidth = this.display.width;
      if (screenWidth >= 1920) return "80vw";
      if (screenWidth >= 1280) return "90vw";
      return "95vw";
    },
    roseLayout() {
      const screenWidth = this.display.width;
      const marginSettings =
        screenWidth >= 1920
          ? { l: 80, r: 80, t: 80, b: 80 }
          : screenWidth >= 960
            ? { l: 50, r: 50, t: 50, b: 50 }
            : { l: 25, r: 25, t: 50, b: 50 };

      return {
        shapes: [],
        annotations: [],
        polar: {
          radialaxis: {
            dtick: this.dtick,
            ticksuffix: "%",
            angle: 45,
          },
          barmode: "stack",
          bargap: 0,
          angularaxis: { direction: "clockwise" },
        },
        autoresize: true,
        autosize: true,
        margin: marginSettings,
        showlegend: true,
        title: {
          text: "Wind Speed [Knots]",
          font: { size: screenWidth >= 1920 ? 16 : 14 },
        },
        legend: {
          orientation: "h",
          font: { family: "sans-serif", size: 14 },
          title: { text: "" },
          yanchor: "top",
          y: -0.3,
          xanchor: "center",
          x: 0.5,
        },
      };
    },
    layout() {
      const screenWidth = this.display.width;
      const marginSettings =
        screenWidth >= 1920
          ? { b: 80, l: 80, r: 40, t: 40 }
          : screenWidth >= 960
            ? { b: 60, l: 60, r: 20, t: 20 }
            : { b: 50, l: 50, r: 20, t: 20 };

      let annotations = [];
      let shapes = [];

      // Find the maximum wind speed in the data (including gusts)
      let maxSpeed = 15; // Default minimum range
      if (this.data.length > 0) {
        const speeds = this.data.flatMap(d => d.y);
        maxSpeed = Math.max(...speeds, maxSpeed);
      }

      // Add some padding to the max speed and round up to the next interval
      const paddedMax = Math.ceil((maxSpeed * 1.2) / 10) * 10;

      // Create intervals based on actual data range
      const verticalIntervals = [
        {
          label: "Light Winds (<6 knots)",
          labelLocation: 3,
          upper: 6,
          lower: 0,
          color: windPlotColours[0],
          opacity: 0.8
        },
        {
          label: "Moderate Winds (6-15 knots)",
          labelLocation: 10,
          upper: 15,
          lower: 6,
          color: windPlotColours[1],
          opacity: 0.8
        },
        {
          label: "Fresh Winds (15-20 knots)",
          labelLocation: 17,
          upper: 20,
          lower: 15,
          color: windPlotColours[2],
          opacity: 0.8
        }
      ];
      if (paddedMax > 20) {
        verticalIntervals.push({
          label: "Strong Winds (20-33 knots)",
          labelLocation: 26,
          upper: 33,
          lower: 20,
          color: windPlotColours[3],
          opacity: 0.8
        });
      }
      if (paddedMax > 33) {
        verticalIntervals.push({
          label: "Near Gale (33-47 knots)",
          labelLocation: 40,
          upper: 47,
          lower: 33,
          color: windPlotColours[4],
          opacity: 0.8
        });
      }
      if (paddedMax > 47) {
        verticalIntervals.push({
          label: "Gale Force (>47 knots)",
          labelLocation: 52,
          upper: paddedMax,
          lower: 47,
          color: windPlotColours[5],
          opacity: 0.8
        });
      }

      [annotations, shapes] = generateShapes(verticalIntervals, {
        color: "#000000",
        bgcolor: "rgba(255, 255, 255, 0.7)"
      });

      return {
        shapes,
        annotations,
        autoresize: true,
        autosize: true,
        hovermode: "x unified",
        showlegend: false,
        margin: marginSettings,
        xaxis: {
          zeroline: true,
          showgrid: false,
        },
        yaxis: {
          showgrid: false,
          zeroline: false,
          title: { text: "Wind Speed [Knots]", font: { color: "#000000" } },
          ticks: "inside",
          font: { color: "#000000" },
          range: [0, paddedMax],
        },
      };
    },
  },

  methods: {
    direction2txt,
    windspeed2txt,
    
getBeaufortColor(force) {
  // Map 13 Beaufort levels (0-12) to  6 color scheme
  if (force <= 1) return windPlotColours[0];      // Light Winds color for Calm & Light Air (0-1)
  if (force <= 3) return windPlotColours[0];      // Light Winds color for Light & Gentle Breeze (2-3)
  if (force <= 5) return windPlotColours[1];      // Moderate Winds color for Moderate & Fresh Breeze (4-5)
  if (force <= 7) return windPlotColours[2];      // Fresh Winds color for Strong Breeze & Near Gale (6-7)
  if (force <= 9) return windPlotColours[3];      // Strong Winds color for Gale & Strong Gale (8-9)
  if (force <= 11) return windPlotColours[4];     // Near Gale color for Storm & Violent Storm (10-11)
  return windPlotColours[5];                      // Gale Force color for Hurricane (12)
},

    async refreshData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetchWindData(this.site, 0);

        if (!response.success) {
          this.error = response.error;
          this.handleNoData();
          return;
        }

        if (response.noDataAvailable) {
          this.handleNoData();
          return;
        }

        const sortedData = response.time
          .map((time, index) => ({
            time: new Date(time),
            speed: response.speed[index] || 0,
            gust: response.gust[index] || 0,
            direction: response.direction[index] || 0,
          }))
          .sort((a, b) => a.time - b.time)
          .filter(
            (d) => !isNaN(d.speed) && !isNaN(d.gust) && !isNaN(d.direction)
          );

        if (sortedData.length === 0) {
          this.handleNoData();
          return;
        }

        this.hasData = true;
        this.$emit("noData", { type: "wind", hasData: true });

        const latestData = sortedData[sortedData.length - 1];
        this.latestDataPoint = {
          speed: latestData.speed,
          gust: latestData.gust,
          direction: latestData.direction,
        };

        this.latestTimestamp = latestData.time;
        this.$emit("latestDate", this.latestTimestamp);

        // Create plot data
        const speedData = {
          x: sortedData.map((d) => d.time),
          y: sortedData.map((d) => d.speed),
          type: "scatter",
          mode: "lines",
          name: "Speed",
          hovertemplate: "Wind speed: %{y:.2f} knots<br>%{x}<extra></extra>",
          line: {
            color: '#fff',
            width: 2,
          },
        };

        const gustData = {
          x: sortedData.map((d) => d.time),
          y: sortedData.map((d) => d.gust),
          type: "scatter",
          mode: "lines",
          name: "Gust",
          hovertemplate: "Wind gust: %{y:.2f} knots<br>%{x}<extra></extra>",
          line: {
            color: '#fff',
            dash: "dot",
            width: 1,
          },
        };

        // Generate rose chart
        const directions = sortedData.map((d) => d.direction);
        if (directions.some((d) => !isNaN(d))) {
const rose = generateRose(
  {
    direction: directions,
    magnitude: sortedData.map((d) => d.speed),
  },
  22.5,
  "Knots"  // Remove the colorScheme parameter
);

          this.roseData = rose[0];
          this.dtick = rose[1];
        } else {
          this.roseData = [];
        }

        this.data = [speedData, gustData];
      } catch (error) {
        this.error = "Unable to fetch wind data";
        this.handleNoData();
      } finally {
        this.loading = false;
        if (!this._isDestroyed) {
          clearTimeout(this.refresher);
          this.refresher = setTimeout(this.refreshData, this.interval);
        }
      }
    },

    handleNoData() {
      this.clearData();
      this.$emit("noData", { type: "wind", hasData: false });
    },

    clearData() {
      this.hasData = false;
      this.latestDataPoint = {
        speed: null,
        gust: null,
        direction: null,
      };
      this.roseData = [];
      this.data = [];
      this.latestTimestamp = null;
    },
  },

  watch: {
    site: {
      immediate: true,
      handler() {
        clearTimeout(this.refresher);
        this.clearData();
        this.refreshData();
      },
    },
  },

  created() {
    this.refreshData();
  },

  beforeUnmount() {
    clearTimeout(this.refresher);
    this.$emit("noData", { type: "wind", hasData: true });
  },
};
</script>

<style scoped>
.beaufort-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: .5rem;
  margin-top: 1rem;
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 8px;
  padding-bottom: 2rem;

}

.help-alert {
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.help-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  position: sticky;
  top: 0;
  background: inherit;
  z-index: 1;
}

.beaufort-card {
  border-radius: 8px;
  transition: transform 0.2s;
  overflow: hidden; 
  background: white; 
    border: .5px solid rgba(251, 251, 251, 0.344);
}

.card-content {
  padding: 8px;
}

.color-strip {
  width: 16px;
  min-height: 100%;
}

.beaufort-card:hover {
  transform: translateY(-2px);
}

.force-number {
  font-size: 1.5rem;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.force-name {
  font-size: 1.1rem;
  flex-grow: 1;
  text-align: right;
}

.wind-speed {
  font-size: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.sea-conditions {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: flex-start;
}

.sea-conditions .v-icon {
  margin-top: 4px;
}

.chart-container {
  position: relative;
  width: 100%;
  max-height: 100vh;
  min-width: 100vw;
  min-height: 320px;
  overflow-y: auto;
}

.help-top-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

/* Responsive styles */
@media (min-width: 960px) and (max-width: 1919px) {
  .chart-container {
    min-width: 600px;
    min-height: 500px;
    overflow-y: auto;
  }
}

@media (min-width: 1920px) {
  .chart-container {
    min-width: 1200px;
    min-height: 1000px;
  }
}

@media (max-width: 600px) {
  .beaufort-grid {
    grid-template-columns: 1fr;
    gap: 0.45rem;
    max-height: 60vh;
    padding-bottom: 3rem;
  }
  
  .beaufort-card {
    margin-bottom: 0.5rem;
  }

  .force-name {
    font-size: 1rem;
  }

  .wind-speed, .sea-conditions {
    font-size: 0.9rem;
  }

  .top-row {
    flex-direction: row;
  }

  .chart-container {
    overflow-y: auto;
    max-height: 90vh;
    margin-top: 4rem;
  }

  .charts-row {
    flex-direction: column;
    gap: 20px;
  }
}

.plotly-chart {
  position: relative;
  width: 100%;
  height: 100%;
}

.charts-row {
  flex-grow: 1;
}

.top-p {
  margin-bottom: 1rem;
}
</style>